import classNames from 'classnames';
import { IHunterTireResult, IHunterVehicle } from '../../API/GoMoto';
import { HunterPassFail, HunterServiceStatusType, HunterTireResult } from '../../API/Enums';
import { images as hunter } from '../../assets/img/hunter';
import { useLocalization } from '../../context/Localization/localization';
import { ImageHelper } from '../../controls/ImageHelper/ImageHelper';
import { isNumeric } from '../../helpers/Number';
import { default as css } from './QuickDrive.module.scss';

enum FrontRear {
    front = 'front',
    rear = 'rear'
}

enum LeftRight {
    left = 'left',
    right = 'right'
}
const prepImage = (imgstr: string) => (imgstr.startsWith('https://') ? imgstr : `data:image/png;base64,${imgstr}`);
const toeString = (toeStatus: HunterTireResult, toeValue: number) => {
    if (toeStatus === HunterTireResult.good || toeStatus === HunterTireResult.noResult) {
        return 'straight';
    }
    else if (toeValue > 0) {
        return 'in';
    }
    else if (toeValue < 0) {
        return 'out';
    }

    return '';
};
const getTireData = (hunterVehicle: IHunterVehicle) => ({
    front: {
        left: resultToTireData(FrontRear.front, LeftRight.left, hunterVehicle.frontLeftResult, hunterVehicle.frontTotalToeResult, hunterVehicle.frontTotalToeValue),
        right: resultToTireData(
            FrontRear.front,
            LeftRight.right,
            hunterVehicle.frontRightResult,
            hunterVehicle.frontTotalToeResult,
            hunterVehicle.frontTotalToeValue,
        ),
        toe: {
            camber: {
                status: hunterVehicle.frontTotalToeResult,
                value: hunterVehicle.frontTotalToeValue,
                fishy: !isNumeric(hunterVehicle.frontTotalToeValue),
            },
        },
    },
    rear: {
        left: resultToTireData(FrontRear.rear, LeftRight.left, hunterVehicle.rearLeftResult, hunterVehicle.rearTotalToeResult, hunterVehicle.rearTotalToeValue),
        right: resultToTireData(FrontRear.rear, LeftRight.right, hunterVehicle.rearRightResult, hunterVehicle.rearTotalToeResult, hunterVehicle.rearTotalToeValue),
        toe: {
            camber: {
                status: hunterVehicle.rearTotalToeResult,
                value: hunterVehicle.rearTotalToeValue,
                fishy: !isNumeric(hunterVehicle.rearTotalToeValue),
            },
        },
    },
});
const resultToTireData = (frontOrRear: FrontRear, leftOrRight: LeftRight, result: IHunterTireResult, toeResult: number, toeValue: number): ITireData => ({
    camber: {
        status: result.camberResult,
        value: result.camberValue,
        fishy: !isNumeric(result.camberValue),
        image: getTireImage(frontOrRear, leftOrRight, toeResult, toeValue, result.camberResult, result.camberValue),
    },
    tread: {
        ...getTreadData(
            (result.tire1 ?? result.tire2 ?? result.tire3 ?? result.tire4) !== null
                ? [
                    {
                        status: result.tire1Result,
                        value: result.tire1,
                    },
                    {
                        status: result.tire2Result,
                        value: result.tire2,
                    },
                    {
                        status: result.tire3Result,
                        value: result.tire3,
                    },
                    {
                        status: result.tire4Result,
                        value: result.tire4,
                    },
                ]
                : [],
        ),
        image: result.image ? prepImage(result.image) : hunter.unknownTireTread,
    },
});
const getTireImage = (frontOrRear: FrontRear, leftOrRight: LeftRight, toeResult: number, toeValue = 0, camberResult: HunterTireResult, camberValue = 0) => {
    const toe = toeString(toeResult, toeValue);
    const [camber, camberColor] = camberString(camberResult, camberValue, toeResult);
    let tireImage = '';
    if (toe && camber && camberColor) {
        // TODO: Make this more robust to check against valid values.
        tireImage = hunter[frontOrRear][leftOrRight].toe[toe].camber[camber][camberColor];
    }

    if (!tireImage) {
        tireImage = hunter[frontOrRear][leftOrRight].toe.straight.camber.straight.gray1;
    }

    return tireImage;
};
const camberString = (
    camberStatus: HunterTireResult,
    camberValue: number,
    toeStatus: HunterTireResult,
): ['straight', 'green' | 'gray' | 'gray1' | 'red'] | ['pos' | 'neg', 'red'] | [] => {
    const badCamberResult: boolean = [HunterTireResult.danger, HunterTireResult.severe, HunterTireResult.warning].includes(camberStatus);
    const badToeResult: boolean = [HunterTireResult.danger, HunterTireResult.severe, HunterTireResult.warning].includes(toeStatus);

    if (camberStatus === HunterTireResult.good && toeStatus === HunterTireResult.good) {
        return ['straight', 'green'];
    }
    else if (badCamberResult && camberValue > 0) {
        return ['pos', 'red'];
    }
    else if (badCamberResult && camberValue < 0) {
        return ['neg', 'red'];
    }
    else if (badToeResult || badCamberResult) {
        return ['straight', 'red'];
    }
    else if (
        (toeStatus === HunterTireResult.noResult && (camberStatus === HunterTireResult.noResult || camberStatus === HunterTireResult.good)) ||
        (toeStatus === HunterTireResult.good && camberStatus === HunterTireResult.noResult)
    ) {
        return ['straight', 'gray'];
    }

    return [];
};
const getTreadData = (tread: { status: HunterTireResult; value?: string; }[]) => {
    const treadRegex = /^(\d+)\/32$/;
    if (tread.length) {
        return (tread as { status: HunterTireResult; value?: string; fishy: boolean; marginal: boolean; }[])
            .filter((elem) => elem.value)
            .reduce(
                (resultObj, tuple) => {
                    const oldTreadValue = parseInt(treadRegex.exec(resultObj.value!)![1], 10);
                    const newTreadValue = parseInt(treadRegex.exec(tuple.value!)![1], 10);
                    const lesserTread = Math.min(oldTreadValue, newTreadValue);
                    const lesserStatus = Math.max(resultObj.status, tuple.status) as HunterTireResult;
                    const leastResultTuple = {
                        ...resultObj,
                        status: lesserStatus,
                        value: `${lesserTread}/32`,
                        marginal: resultObj.marginal || lesserStatus > HunterTireResult.good,
                    };

                    return leastResultTuple;
                },
                { value: '8/32',
                    status: HunterTireResult.noResult,
                    fishy: false,
                    marginal: false },
            );
    }
    else {
        return { status: HunterTireResult.noResult,
            fishy: true,
            marginal: false };
    }
};

// #region Data objects
interface ITireData {
    label?: string;
    camber: {
        status: HunterTireResult;
        value?: number;
        image?: string;
        fishy: boolean;
    };
    tread?: {
        status: HunterTireResult;
        value?: string;
        image?: string;
        marginal: boolean;
        fishy: boolean;
    };
}

export type QDVehicle = ReturnType<ReturnType<typeof useQDVehicleFactory>>;

export const useQDVehicleFactory = () => (hunterVehicle: IHunterVehicle) => {
    const getAlignment = (tireData: Record<FrontRear, Record<LeftRight | 'toe', ITireData>>) => {
        const alignment = {
            recommended: false,
            text: { short: (localization: ReturnType<typeof useLocalization>) => '',
                long: (localization: ReturnType<typeof useLocalization>) => '' },
            fishy: false,
            iconImg: '',
            hunterServiceStatusType: HunterServiceStatusType.OK,
        };
        alignment.recommended = hunterVehicle.wheelAlignmentValue === HunterPassFail.fail;
        alignment.hunterServiceStatusType = alignment.recommended ? HunterServiceStatusType.Recommended : HunterServiceStatusType.OK;

        alignment.fishy =
            tireData.front.left.camber.fishy ||
            tireData.front.right.camber.fishy ||
            tireData.rear.right.camber.fishy ||
            tireData.rear.left.camber.fishy ||
            tireData.front.toe.camber.fishy ||
            tireData.rear.toe.camber.fishy;

        if (alignment.recommended) {
            alignment.text = {
                short: (localization: ReturnType<typeof useLocalization>) => localization.AlignmentFailed as string,
                long: (localization: ReturnType<typeof useLocalization>) => localization.AlignmentFailedFull as string,
            };
            alignment.iconImg = hunter.icon.alignment.red;
        }
        else if (!alignment.recommended && alignment.fishy) {
            alignment.text = {
                short: (localization: ReturnType<typeof useLocalization>) =>
                    localization.formatString(localization.HunterCheckNoData, localization.Alignment) as string,
                long: (localization: ReturnType<typeof useLocalization>) =>
                    localization.formatString(localization.HunterCheckNoDataFull, localization.Alignment.toLocaleLowerCase()) as string,
            };
            alignment.iconImg = hunter.icon.alignment.grey;
        }
        else {
            alignment.text = {
                short: (localization: ReturnType<typeof useLocalization>) => localization.AlignmentPassed as string,
                long: (localization: ReturnType<typeof useLocalization>) => localization.AlignmentPassedFull as string,
            };
            alignment.iconImg = hunter.icon.alignment.green;
        }

        return alignment;
    };

    const getTires = (tireData: Record<FrontRear, Record<LeftRight, ITireData>>) => {
        const tires = {
            recommended: false,
            text: { short: (localization: ReturnType<typeof useLocalization>) => '',
                long: (localization: ReturnType<typeof useLocalization>) => '' },
            fishy: false,
            marginal: false,
            iconImg: '',
            hunterServiceStatusType: HunterServiceStatusType.OK,
        };

        tires.recommended = hunterVehicle.tireCheckValue === HunterPassFail.fail;

        tires.fishy =
            tireData.front.left.tread?.fishy !== false ||
            tireData.front.right.tread?.fishy !== false ||
            tireData.rear.right.tread?.fishy !== false ||
            tireData.rear.left.tread?.fishy !== false;

        tires.marginal =
            tireData.front.left.tread?.marginal !== false ||
            tireData.front.right.tread?.marginal !== false ||
            tireData.rear.right.tread?.marginal !== false ||
            tireData.rear.left.tread?.marginal !== false;

        tires.hunterServiceStatusType = tires.recommended
            ? HunterServiceStatusType.Recommended
            : tires.marginal
                ? HunterServiceStatusType.Marginal
                : tires.fishy
                    ? HunterServiceStatusType.NoData
                    : HunterServiceStatusType.OK;

        if (tires.recommended) {
            tires.text = {
                short: (localization: ReturnType<typeof useLocalization>) => localization.TireTreadFailed as string,
                long: (localization: ReturnType<typeof useLocalization>) => localization.TireTreadFailedFull as string,
            };
            tires.iconImg = hunter.icon.tire.red;
        }
        else if (!tires.recommended && tires.fishy) {
            tires.text = {
                short: (localization: ReturnType<typeof useLocalization>) => localization.formatString(localization.HunterCheckNoData, localization.Tire) as string,
                long: (localization: ReturnType<typeof useLocalization>) =>
                    localization.formatString(localization.HunterCheckNoDataFull, localization.Tire.toLocaleLowerCase()) as string,
            };
            tires.iconImg = hunter.icon.tire.orange;
        }
        else if (!tires.recommended && tires.marginal) {
            tires.text = {
                short: (localization: ReturnType<typeof useLocalization>) =>
                    localization.formatString(localization.HunterCheckMarginal, localization.TireTreadDepth) as string,
                long: (localization: ReturnType<typeof useLocalization>) =>
                    localization.formatString(localization.HunterCheckMarginalFull, localization.TireTreadDepth.toLocaleLowerCase()) as string,
            };
            tires.iconImg = hunter.icon.tire.orange;
        }
        else {
            tires.text = {
                short: (localization: ReturnType<typeof useLocalization>) => localization.TireTreadPassed as string,
                long: (localization: ReturnType<typeof useLocalization>) => localization.TireTreadPassedFull as string,
            };
            tires.iconImg = hunter.icon.tire.green;
        }

        return tires;
    };

    const drawAlignmentGraphic = () => (
        <div className={css.alignmentGraphic}>
            <div className={css.alignmentHalf}>
                <div className={classNames(css.alignmentQuarter, css.front, css.left)}>
                    <ImageHelper
                        imageProps={{ src: tireData[FrontRear.front][LeftRight.left].camber.image,
                            alt: tireData[FrontRear.front][LeftRight.left].label }}
                    />
                </div>
                <div className={classNames(css.alignmentQuarter, css.front, css.right)}>
                    <ImageHelper
                        imageProps={{
                            src: tireData[FrontRear.front][LeftRight.right].camber.image,
                            alt: tireData[FrontRear.front][LeftRight.left].label,
                        }}
                    />
                </div>
            </div>
            <div className={css.alignmentHalf}>
                <div className={classNames(css.alignmentQuarter, css.rear, css.left)}>
                    <ImageHelper
                        imageProps={{
                            src: tireData[FrontRear.rear][LeftRight.left].camber.image,
                            alt: tireData[FrontRear.front][LeftRight.left].label,
                        }}
                    />
                </div>
                <div className={classNames(css.alignmentQuarter, css.rear, css.right)}>
                    <ImageHelper
                        imageProps={{
                            src: tireData[FrontRear.rear][LeftRight.right].camber.image,
                            alt: tireData[FrontRear.front][LeftRight.left].label,
                        }}
                    />
                </div>
            </div>
        </div>
    );

    const tireData = getTireData(hunterVehicle);
    const vehicleImage = hunterVehicle.image ? prepImage(hunterVehicle.image) : hunter.unknownCarImage;
    const alignment = getAlignment(tireData);
    const tires = getTires(tireData);

    return { tireData,
        vehicleImage,
        alignment,
        tires,
        drawAlignmentGraphic };
};
